import type { PriceChangeLine } from "./priceChangeSnapshot.js";

function pdfEscape(value: string): string {
  return value
    .replace(/[^\x20-\x7E]/g, "?")
    .replace(/\\/g, "\\\\")
    .replace(/\(/g, "\\(")
    .replace(/\)/g, "\\)");
}

function clip(value: string, max: number): string {
  const text = value.replace(/\s+/g, " ").trim();
  return text.length <= max ? text : `${text.slice(0, max - 1)}…`;
}

function money(value: number | null): string {
  return value == null ? "" : value.toFixed(2);
}

function rowText(line: PriceChangeLine): string {
  return [
    clip(line.code, 16).padEnd(16),
    clip(line.kind, 8).padEnd(8),
    clip(line.group_name, 14).padEnd(14),
    clip(line.roof_style, 12).padEnd(12),
    money(line.current_wholesale_usd).padStart(10),
    money(line.current_msrp_usd).padStart(10),
    money(line.suggested_wholesale_usd).padStart(10),
    money(line.suggested_msrp_usd).padStart(10),
    money(line.new_wholesale_usd).padStart(10),
    money(line.new_msrp_usd).padStart(10),
  ].join(" ");
}

const HEADER = [
  "Code".padEnd(16),
  "Kind".padEnd(8),
  "Group".padEnd(14),
  "Style".padEnd(12),
  "WS now".padStart(10),
  "MSRP now".padStart(10),
  "WS COGS".padStart(10),
  "MSRP COGS".padStart(10),
  "New WS".padStart(10),
  "New MSRP".padStart(10),
].join(" ");

function wrapNote(notes: string): string[] {
  const words = notes.replace(/\s+/g, " ").trim().split(" ").filter(Boolean);
  if (!words.length) return [];
  const lines: string[] = [];
  let current = "";
  for (const word of words) {
    const next = current ? `${current} ${word}` : word;
    if (next.length > 110) {
      if (current) lines.push(current);
      current = word.slice(0, 110);
    } else {
      current = next;
    }
    if (lines.length >= 6) break;
  }
  if (current && lines.length < 6) lines.push(current);
  return lines;
}

function pageStream(title: string, body: string[]): string {
  const commands = ["BT", "/F1 9 Tf", "36 576 Td", "14 TL", `(${pdfEscape(title)}) Tj`, "T*"];
  for (const line of body) {
    commands.push(`(${pdfEscape(line)}) Tj`, "T*");
  }
  commands.push("ET");
  return commands.join("\n");
}

/** Landscape letter PDF of a named price-change program. */
export function priceChangeSnapshotPdf(input: {
  name: string;
  notes: string;
  lines: readonly PriceChangeLine[];
  committedAt?: string | null;
}): Uint8Array {
  const title = clip(input.name || "Price change", 80);
  const stamp = input.committedAt ? `Committed ${input.committedAt.slice(0, 10)}` : "Draft";
  const intro = [`${stamp}  ·  ${input.lines.length} items`, ...wrapNote(input.notes), "", HEADER];
  const pages: string[][] = [];
  let current = [...intro];
  const limit = 36;
  for (const line of input.lines) {
    if (current.length >= limit) {
      pages.push(current);
      current = [HEADER];
    }
    current.push(rowText(line));
  }
  if (!pages.length || current.length) pages.push(current);
  const streams = pages.map((body, index) =>
    pageStream(`${title}  (${index + 1}/${pages.length})`, body),
  );
  return buildPdf(streams);
}

function buildPdf(streams: string[]): Uint8Array {
  const chunks: string[] = ["%PDF-1.4\n"];
  let length = chunks[0].length;
  const offsets: number[] = [];
  function add(id: number, body: string) {
    offsets[id] = length;
    const text = `${id} 0 obj\n${body}\nendobj\n`;
    chunks.push(text);
    length += text.length;
  }
  const fontId = 3;
  let next = 4;
  const pageIds: number[] = [];
  const contentIds: number[] = [];
  streams.forEach(() => {
    contentIds.push(next);
    pageIds.push(next + 1);
    next += 2;
  });
  add(1, "<< /Type /Catalog /Pages 2 0 R >>");
  add(2, `<< /Type /Pages /Kids [${pageIds.map((id) => `${id} 0 R`).join(" ")}] /Count ${pageIds.length} >>`);
  add(fontId, "<< /Type /Font /Subtype /Type1 /BaseFont /Courier >>");
  streams.forEach((stream, index) => {
    add(contentIds[index], `<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`);
    add(
      pageIds[index],
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 792 612] /Contents ${contentIds[index]} 0 R /Resources << /Font << /F1 ${fontId} 0 R >> >> >>`,
    );
  });
  const xrefAt = length;
  const size = next;
  let xref = `xref\n0 ${size}\n0000000000 65535 f \n`;
  for (let id = 1; id < size; id += 1) {
    xref += `${String(offsets[id] ?? 0).padStart(10, "0")} 00000 n \n`;
  }
  xref += `trailer\n<< /Size ${size} /Root 1 0 R >>\nstartxref\n${xrefAt}\n%%EOF`;
  chunks.push(xref);
  const bytes = new Uint8Array(length + xref.length);
  let offset = 0;
  for (const chunk of chunks) {
    for (let i = 0; i < chunk.length; i += 1) bytes[offset + i] = chunk.charCodeAt(i);
    offset += chunk.length;
  }
  return bytes;
}
